import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { VenuePicker } from '../components/VenuePicker';
import { timeAgo } from '../lib/timeAgo';
import {
  APP_STORE_URL,
  fetchCityRankedVenues,
  fetchRatedCities,
  fetchVenueRankContext,
  formatScore,
  loadMockupReviews,
  saveMockupReviews,
  venueDisplayName,
  type GrowthCity,
  type GrowthVenue,
  type MockupReviews,
  type VenueRankContext,
} from '../lib/growth';

type MockupId = 'city-page' | 'venue-one-pager' | 'outreach-email';

const MOCKUPS: { id: MockupId; label: string; blurb: string }[] = [
  {
    id: 'city-page',
    label: 'City page',
    blurb:
      'The programmatic GEO surface — shado.app/nightlife/{city}, one per metro, rendered from live rankings. This mockup IS the spec for the public web build.',
  },
  {
    id: 'venue-one-pager',
    label: 'Venue one-pager',
    blurb:
      'The outreach opener — a hosted “your Shado stats” page personalized per venue. A link works in any channel; this is what gets pasted into the IG DM.',
  },
  {
    id: 'outreach-email',
    label: 'Outreach email',
    blurb:
      'The merge-field email module for venues reachable by email. Secondary channel to IG DMs — real sending needs a separate domain + ESP before volume.',
  },
];

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function typeChip(t: string | null): string {
  if (!t) return 'venue';
  return t.replace(/_/g, ' ');
}

/** Minimal browser chrome around a rendered page mockup. */
function BrowserFrame({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-borderStrong bg-[#0B0C0E] shadow-2xl">
      <div className="flex items-center gap-3 border-b border-border bg-surfaceElevated px-4 py-2.5">
        <span className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#3A3B3F]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#3A3B3F]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#3A3B3F]" />
        </span>
        <span className="flex-1 truncate rounded-md border border-border bg-canvas px-3 py-1 text-[11px] tabular-nums text-muted">
          {url}
        </span>
      </div>
      {children}
    </div>
  );
}

/** Minimal mail-client chrome around the email module. */
function EmailFrame({
  from,
  to,
  subject,
  children,
}: {
  from: string;
  to: string;
  subject: string;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-borderStrong bg-[#F4F4F6] shadow-2xl">
      <div className="space-y-1 border-b border-black/10 bg-white px-5 py-3.5 text-[12px] text-black/70">
        <div>
          <span className="text-black/40">From&nbsp;&nbsp;</span>
          {from}
        </div>
        <div>
          <span className="text-black/40">To&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
          {to}
        </div>
        <div className="font-semibold text-black/90">{subject}</div>
      </div>
      <div className="p-4 sm:p-6">{children}</div>
    </div>
  );
}

function ShadoMark({ light = false }: { light?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-[12px] w-[12px] rounded-[3.5px] bg-accent shadow-[0_0_0_3px_rgba(139,92,246,0.14)]" />
      <span
        className={`font-display text-[15px] font-semibold tracking-[-0.02em] ${light ? 'text-white' : 'text-textPrimary'}`}
      >
        Shado
      </span>
    </span>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-4 py-3 text-center">
      <div className="font-display text-[22px] font-semibold tabular-nums tracking-[-0.02em] text-textPrimary">
        {value}
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-[0.08em] text-mutedDim">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The three mockup bodies
// ---------------------------------------------------------------------------

function CityPageMockup({ city, venues }: { city: GrowthCity; venues: GrowthVenue[] }) {
  const cityName = city.name ?? 'City';
  return (
    <BrowserFrame url={`shado.app/nightlife/${slugify(cityName)}`}>
      <div className="px-5 py-8 sm:px-10">
        <ShadoMark />
        <div className="mt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
          City guide · updated weekly
        </div>
        <h1 className="mt-2 font-display text-[28px] font-semibold leading-tight tracking-[-0.02em] text-white sm:text-[34px]">
          The best nightlife in {cityName}
        </h1>
        <p className="mt-2 max-w-lg text-[13px] leading-relaxed text-muted">
          Ranked by 18–25-year-olds actually going out — head-to-head comparisons, not five-star
          averages.
        </p>

        <div className="mt-7 space-y-1.5">
          {venues.slice(0, 10).map((v, i) => (
            <div
              key={v.id}
              className="flex items-center gap-3.5 rounded-lg border border-border/70 bg-surface px-4 py-3"
            >
              <span className="w-6 shrink-0 text-right font-display text-[16px] font-semibold tabular-nums text-accent">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-medium text-textPrimary">{venueDisplayName(v)}</div>
                <div className="mt-0.5 text-[11px] capitalize text-mutedDim">
                  {typeChip(v.venue_type)} · {v.venue_shado_unique_users ?? 0} rater
                  {(v.venue_shado_unique_users ?? 0) === 1 ? '' : 's'}
                </div>
              </div>
              <span className="shrink-0 rounded-md bg-accent/10 px-2.5 py-1 font-display text-[15px] font-semibold tabular-nums text-[#b79cff]">
                {formatScore(v.venue_shado_score)}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-7 rounded-lg border border-border bg-surfaceElevated px-4 py-3.5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-mutedDim">
            How this ranking works
          </div>
          <p className="mt-1.5 text-[12px] leading-relaxed text-muted">
            Shado scores come from head-to-head picks — every rating compares tonight’s venue against
            places the rater already knows. No paid placement, no five-star inflation.
          </p>
        </div>

        <div className="mt-7 flex flex-col items-start gap-3 rounded-xl border border-accent/25 bg-accent/[0.07] px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[14px] font-semibold text-white">Think this ranking is wrong?</div>
            <div className="mt-0.5 text-[12px] text-muted">Rate {cityName}’s bars yourself.</div>
          </div>
          <span className="rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-white">
            Get Shado on the App Store
          </span>
        </div>
      </div>
    </BrowserFrame>
  );
}

function VenueOnePagerMockup({ ctx }: { ctx: VenueRankContext }) {
  const name = venueDisplayName(ctx.venue);
  const headline =
    ctx.rank != null && ctx.totalRatedInCity != null && ctx.cityName
      ? `${name} is ranked #${ctx.rank} of ${ctx.totalRatedInCity} in ${ctx.cityName}`
      : `Students are already rating ${name} on Shado`;
  return (
    <BrowserFrame url={`shado.app/v/${slugify(name)}`}>
      <div className="px-5 py-8 sm:px-10">
        <div className="flex items-center justify-between">
          <ShadoMark />
          <span className="text-[10px] uppercase tracking-[0.12em] text-mutedDim">
            Prepared for {name}
          </span>
        </div>
        <h1 className="mt-7 max-w-xl font-display text-[26px] font-semibold leading-tight tracking-[-0.02em] text-white sm:text-[30px]">
          {headline}
        </h1>
        <p className="mt-2.5 max-w-lg text-[13px] leading-relaxed text-muted">
          Shado is where 18–25-year-olds rank where to go out. These numbers are live — your
          customers put them there.
        </p>

        <div className="mt-6 grid max-w-md grid-cols-3 gap-3">
          <StatBlock label="Score" value={formatScore(ctx.venue.venue_shado_score)} />
          <StatBlock label="Raters" value={String(ctx.venue.venue_shado_unique_users ?? 0)} />
          <StatBlock label="City rank" value={ctx.rank != null ? `#${ctx.rank}` : '—'} />
        </div>

        <div className="mt-7 max-w-xl rounded-xl border border-border bg-surface px-5 py-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
            What you get — free
          </div>
          <ul className="mt-3 space-y-2 text-[13px] text-muted">
            <li className="flex gap-2.5">
              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-accent" />
              A printed QR kit (table tents + stickers) so your customers rate you where it counts.
            </li>
            <li className="flex gap-2.5">
              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-accent" />
              Your live stats — score, raters, and where you stand in {ctx.cityName ?? 'your city'}.
            </li>
            <li className="flex gap-2.5">
              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-accent" />
              First look at your venue page before the public web launch.
            </li>
          </ul>
        </div>

        <div className="mt-7 flex max-w-xl items-center gap-5 rounded-xl border border-accent/25 bg-accent/[0.07] px-5 py-4">
          <span className="shrink-0 rounded-lg bg-white p-2">
            <QRCodeSVG value={APP_STORE_URL} size={72} fgColor="#08090A" bgColor="#FFFFFF" />
          </span>
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-white">Rate us on Shado</div>
            <p className="mt-1 text-[12px] leading-relaxed text-muted">
              This is the table-tent your customers will see — scan, rate, compare.
            </p>
          </div>
        </div>
      </div>
    </BrowserFrame>
  );
}

function OutreachEmailMockup({ ctx }: { ctx: VenueRankContext }) {
  const name = venueDisplayName(ctx.venue);
  const rankLine =
    ctx.rank != null && ctx.cityName ? `#${ctx.rank} in ${ctx.cityName}` : 'already being rated';
  return (
    <EmailFrame
      from="Evan from Shado <evan@get.shado.app>"
      to={`${name} <owner@${slugify(name)}.com>`}
      subject={`${name} is ${rankLine} on Shado`}
    >
      <div className="mx-auto max-w-[520px] overflow-hidden rounded-xl bg-[#0B0C0E]">
        <div className="px-7 pt-7">
          <ShadoMark light />
        </div>
        <div className="px-7 pb-7 pt-5">
          <h2 className="font-display text-[21px] font-semibold leading-snug tracking-[-0.01em] text-white">
            Your customers put {name} at {rankLine}.
          </h2>
          <p className="mt-3 text-[13px] leading-relaxed text-[#9A9AA2]">
            Hi {name} team — Shado is the app where college students rank where to go out
            (think Letterboxd, for nightlife). You didn’t sign up for anything: students rated you
            on their own.
          </p>

          <div className="mt-5 grid grid-cols-3 gap-2.5">
            <div className="rounded-lg bg-white/[0.05] px-3 py-2.5 text-center">
              <div className="font-display text-[18px] font-semibold text-white">
                {formatScore(ctx.venue.venue_shado_score)}
              </div>
              <div className="mt-0.5 text-[9px] uppercase tracking-[0.08em] text-[#646469]">Score</div>
            </div>
            <div className="rounded-lg bg-white/[0.05] px-3 py-2.5 text-center">
              <div className="font-display text-[18px] font-semibold text-white">
                {ctx.venue.venue_shado_unique_users ?? 0}
              </div>
              <div className="mt-0.5 text-[9px] uppercase tracking-[0.08em] text-[#646469]">Raters</div>
            </div>
            <div className="rounded-lg bg-white/[0.05] px-3 py-2.5 text-center">
              <div className="font-display text-[18px] font-semibold text-white">
                {ctx.rank != null ? `#${ctx.rank}` : '—'}
              </div>
              <div className="mt-0.5 text-[9px] uppercase tracking-[0.08em] text-[#646469]">
                City rank
              </div>
            </div>
          </div>

          <p className="mt-5 text-[13px] leading-relaxed text-[#9A9AA2]">
            We’d like to send you a free QR table-tent kit so the people already in your venue can
            rate you — the venues that climb these rankings are the ones students see first.
          </p>

          <div className="mt-6">
            <span className="inline-block rounded-lg bg-[#8B5CF6] px-5 py-2.5 text-[13px] font-semibold text-white">
              See your live stats
            </span>
          </div>

          <p className="mt-6 text-[12px] leading-relaxed text-[#9A9AA2]">
            — Evan, founder of Shado
          </p>
        </div>
        <div className="border-t border-white/[0.08] px-7 py-4 text-[10px] leading-relaxed text-[#646469]">
          Shado · nightlife, ranked by students. You’re receiving this one-time note because your
          venue appears in our public rankings.
        </div>
      </div>
    </EmailFrame>
  );
}

// ---------------------------------------------------------------------------
// Review bar — verdicts persist in prod app_config (admin RPCs), so approvals
// survive sessions and are visible to every operator.
// ---------------------------------------------------------------------------

function ReviewBar({
  mockupId,
  reviews,
  readable,
  onSave,
  saving,
}: {
  mockupId: MockupId;
  reviews: MockupReviews;
  readable: boolean;
  onSave: (id: MockupId, verdict: 'approved' | 'needs_work', note: string) => void;
  saving: boolean;
}) {
  const current = reviews[mockupId];
  const [note, setNote] = useState('');

  useEffect(() => {
    setNote(current?.note ?? '');
  }, [current?.note, mockupId]);

  if (!readable) return null;

  return (
    <div className="admin-card mt-4 flex flex-wrap items-center gap-3 !py-3">
      {current ? (
        <span
          className={`admin-status ${current.verdict === 'approved' ? 'admin-status-success' : 'admin-status-warning'}`}
        >
          {current.verdict === 'approved' ? 'Approved' : 'Needs work'} · {timeAgo(current.at)}
        </span>
      ) : (
        <span className="admin-status admin-status-neutral">Not reviewed</span>
      )}
      <input
        className="admin-input !w-auto min-w-[200px] flex-1 !text-[12px]"
        placeholder="Optional note — what should change?"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave(mockupId, 'needs_work', note)}
          className="admin-btn admin-btn-secondary admin-btn-sm"
        >
          Needs work
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave(mockupId, 'approved', note)}
          className="admin-btn admin-btn-primary admin-btn-sm"
        >
          {saving ? 'Saving…' : 'Approve'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function MockupsScreen() {
  const [active, setActive] = useState<MockupId>('city-page');

  const [cities, setCities] = useState<GrowthCity[]>([]);
  const [selectedCityId, setSelectedCityId] = useState<string>('');
  const [cityVenues, setCityVenues] = useState<GrowthVenue[]>([]);
  const [cityLoading, setCityLoading] = useState(true);

  const [venueCtx, setVenueCtx] = useState<VenueRankContext | null>(null);

  const [reviews, setReviews] = useState<MockupReviews>({});
  const [reviewsReadable, setReviewsReadable] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const selectedCity = useMemo(
    () => cities.find((c) => c.id === selectedCityId) ?? null,
    [cities, selectedCityId],
  );

  // Cities + persisted reviews, once.
  useEffect(() => {
    let mounted = true;
    void (async () => {
      const [cityRows, reviewState] = await Promise.all([fetchRatedCities(), loadMockupReviews()]);
      if (!mounted) return;
      setCities(cityRows);
      if (cityRows.length > 0) setSelectedCityId((prev) => prev || cityRows[0].id);
      setReviews(reviewState.reviews);
      setReviewsReadable(reviewState.readable);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Ranked venues for the selected city; also seeds the default venue for the
  // one-pager/email mockups the first time.
  useEffect(() => {
    if (!selectedCityId) return;
    let mounted = true;
    setCityLoading(true);
    void (async () => {
      const rows = await fetchCityRankedVenues(selectedCityId, 50);
      if (!mounted) return;
      setCityVenues(rows);
      setCityLoading(false);
      if (rows.length > 0) {
        setVenueCtx((prev) => prev ?? { venue: rows[0], cityName: null, rank: 1, totalRatedInCity: rows.length });
        // Fill in the city name asynchronously for the seeded venue.
        void fetchVenueRankContext(rows[0]).then((ctx) => {
          if (mounted) setVenueCtx((prev) => (prev && prev.venue.id === ctx.venue.id ? ctx : prev));
        });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedCityId]);

  const pickVenue = useCallback((v: GrowthVenue) => {
    setVenueCtx({ venue: v, cityName: v.locality, rank: null, totalRatedInCity: null });
    void fetchVenueRankContext(v).then((ctx) => {
      setVenueCtx((prev) => (prev && prev.venue.id === ctx.venue.id ? ctx : prev));
    });
  }, []);

  const saveReview = useCallback(
    (id: MockupId, verdict: 'approved' | 'needs_work', note: string) => {
      setSavingReview(true);
      setReviewError(null);
      const next: MockupReviews = {
        ...reviews,
        [id]: { verdict, note: note.trim() || null, at: new Date().toISOString() },
      };
      void saveMockupReviews(next).then((err) => {
        setSavingReview(false);
        if (err) {
          setReviewError(err);
        } else {
          setReviews(next);
        }
      });
    },
    [reviews],
  );

  const activeMeta = MOCKUPS.find((m) => m.id === active)!;

  return (
    <div className="admin-page max-w-4xl mx-auto">
      <PageHeader
        title="Mockups"
        subtitle="Real prototypes on live production data. Approve them here — the approved mockup is the spec."
      />

      {/* Mockup switcher */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {MOCKUPS.map((m) => (
          <button
            key={m.id}
            type="button"
            data-active={active === m.id}
            onClick={() => setActive(m.id)}
            className="admin-pill"
          >
            {m.label}
          </button>
        ))}
      </div>

      <p className="mb-4 max-w-2xl text-[12.5px] leading-relaxed text-muted">{activeMeta.blurb}</p>

      {/* Data controls */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        {active === 'city-page' ? (
          <label className="flex items-center gap-2 text-[12px] text-mutedDim">
            City
            <select
              className="admin-input !w-auto !px-2 !text-[12px]"
              value={selectedCityId}
              onChange={(e) => setSelectedCityId(e.target.value)}
            >
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name ?? '—'} · {c.total_shado_ratings ?? 0} ratings
                </option>
              ))}
            </select>
          </label>
        ) : (
          <VenuePicker current={venueCtx?.venue ?? null} onPick={pickVenue} />
        )}
      </div>

      {/* The mockup itself */}
      {active === 'city-page' ? (
        cityLoading ? (
          <SkeletonRows rows={8} />
        ) : selectedCity && cityVenues.length > 0 ? (
          <>
            <CityPageMockup city={selectedCity} venues={cityVenues} />
            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px] text-mutedDim">
              <span>Ships with:</span>
              {['schema.org ItemList', 'AggregateRating', 'llms.txt', 'sitemap', 'OG cards'].map((t) => (
                <span key={t} className="admin-chip-id">
                  {t}
                </span>
              ))}
            </div>
          </>
        ) : (
          <EmptyState
            title="No rated venues in this city yet"
            hint="Pick a city with ratings — the page renders straight from live rankings."
          />
        )
      ) : venueCtx ? (
        active === 'venue-one-pager' ? (
          <VenueOnePagerMockup ctx={venueCtx} />
        ) : (
          <OutreachEmailMockup ctx={venueCtx} />
        )
      ) : (
        <EmptyState title="Pick a venue" hint="Search any rated venue to fill the merge fields." />
      )}

      {reviewError ? (
        <div className="admin-banner-error mt-4">{reviewError}</div>
      ) : null}

      <ReviewBar
        mockupId={active}
        reviews={reviews}
        readable={reviewsReadable}
        onSave={saveReview}
        saving={savingReview}
      />
    </div>
  );
}
